import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { TargetareStats } from "@/types/targetare";
import { formatDataOra, formatNumar } from "@/utils/targetareFormatting";

type StatisticiTargetareProps = {
  stats: TargetareStats | undefined;
  isLoading: boolean;
};

type IndicatorProps = {
  eticheta: string;
  valoare: string;
  detaliu?: string;
};

function Indicator({ eticheta, valoare, detaliu }: IndicatorProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          {eticheta}
        </p>
        <p className="mt-1 text-2xl font-heading font-bold tabular-nums">
          {valoare}
        </p>
        {detaliu && (
          <p className="mt-1 text-xs text-muted-foreground">{detaliu}</p>
        )}
      </CardContent>
    </Card>
  );
}

export function StatisticiTargetare({
  stats,
  isLoading,
}: StatisticiTargetareProps) {
  if (isLoading || !stats) {
    return (
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-8">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }

  const tier = (t: string) => formatNumar(stats.pe_tier[t] ?? 0);

  return (
    <div className="space-y-2">
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4 lg:grid-cols-8">
        <Indicator
          eticheta="Firme în scop"
          valoare={formatNumar(stats.in_scop)}
          detaliu={`din ${formatNumar(stats.clasificate)} cu potriviri`}
        />
        <Indicator
          eticheta="Tier A"
          valoare={tier("A")}
          detaliu="CAEN principal + dimensiune"
        />
        <Indicator
          eticheta="Tier B"
          valoare={tier("B")}
          detaliu="listă oficială + dimensiune"
        />
        <Indicator
          eticheta="Tier C"
          valoare={tier("C")}
          detaliu="indiferent de dimensiune"
        />
        <Indicator
          eticheta="Tier D"
          valoare={tier("D")}
          detaliu="doar dimensiune"
        />
        <Indicator
          eticheta="Firme active"
          valoare={formatNumar(stats.firme_active)}
          detaliu={`din ${formatNumar(stats.firme)} în snapshot`}
        />
        <Indicator
          eticheta="Bilanțuri"
          valoare={formatNumar(stats.bilanturi)}
          detaliu="situații financiare 2025"
        />
        <Indicator
          eticheta="Emailuri valide"
          valoare={formatNumar(stats.contacte_email_valide)}
          detaliu={`${formatNumar(stats.excluderi)} excluderi`}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Ultima clasificare: {formatDataOra(stats.ultima_clasificare)} · curs
        RON/EUR folosit: {String(stats.setari.curs_ron_eur ?? "–")}
      </p>
    </div>
  );
}
